/**
 * Una conversación en la presentación «Bandeja» (2026-10-06).
 *
 * En vez de burbujas a izquierda y derecha, un hilo de lectura: días, tandas de
 * la misma persona y tus mensajes marcados con una barra de color. Reemplaza al
 * ChatGestor recuperado; conserva su refresco cada 3 s y reutiliza
 * ContenidoMensaje para fotos, audios y documentos.
 *
 * Lo que mandas no se avisa con un toast: se ve al final del hilo con su estado
 * real (enviando, programado, con error) hasta que el bot lo envía. Desde la
 * caja se puede programar (reloj) y usar respuestas rápidas («/atajo»).
 */
import "./gestor.css";
import "./bandeja.css";
import {
  rec_ContenidoMensaje as ContenidoMensaje,
  rec_React as React,
  rec_gr as IconoVolver,
  rec_ii as Girando,
} from "../../runtime/index.jsx";
import { REACCIONES, fechaHora, leerArchivo, opcionesProgramar, tamano } from "./api-modos.js";
import { Avatar } from "./Avatar.jsx";
import { BuscadorChat, ContenidoEspecial, EditorNota, ModalRecordar, ModalTarea, Reacciones } from "./herramientas.jsx";
import { IconoEstrella, IconoPin, IconoReloj } from "./iconos.jsx";
import { agruparHilo, colorDe, horaDe, iniciales } from "./util-gestor.js";

// runtime/index.jsx importa este archivo antes de definir React (import circular).
const useCallback = (...a) => React.useCallback(...a);
const useEffect = (...a) => React.useEffect(...a);
const useMemo = (...a) => React.useMemo(...a);
const useRef = (...a) => React.useRef(...a);
const useState = (...a) => React.useState(...a);

const IconoInfo = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" {...p}>
    <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" />
  </svg>
);
const IconoEnviar = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
    <path d="M4 12l16-8-6 16-2.5-6.5z" /><path d="M11.5 13.5L20 4" />
  </svg>
);

function Mensaje({ m, chat, cliente, acciones }) {
  const [verVersiones, setVerVersiones] = useState(false);
  const [anotando, setAnotando] = useState(false);
  const eliminado = Boolean(m.eliminadoEn);
  const quien = m.deMi ? `ti` : chat.esGrupo ? (m.autor ?? `el autor`) : chat.nombre;
  const especial = ContenidoEspecial({ m });
  return (
    <div className={`gx-msj${eliminado ? ` eliminado` : ``}`} tabIndex={-1}>
      <div className="gx-acciones" role="toolbar" aria-label="Acciones del mensaje">
        {m.citable && REACCIONES.map((e) => (
          <button key={e} type="button" title={`Reaccionar ${e}`} aria-label={`Reaccionar ${e}`} onClick={() => acciones.reaccionar(m, e)}>{e}</button>
        ))}
        {m.citable && <span className="sep" />}
        {m.citable && <button type="button" title="Responder citando" onClick={() => acciones.responder(m)}>↩ Responder</button>}
        <button type="button" title="Recordarme responder" onClick={() => acciones.recordar(m)}>⏰</button>
        <button type="button" title="Nota propia" onClick={() => setAnotando(true)}>📝</button>
        <button type="button" title="Convertir en tarea del planner" onClick={() => acciones.tarea(m)}>✓ Tarea</button>
      </div>
      {m.cita && (
        <div className="gx-cita"><b>{m.cita.deMi ? `Tú` : chat.esGrupo ? `Citado` : chat.nombre}</b>{m.cita.texto ?? `(foto, audio u otro adjunto)`}</div>
      )}
      {m.verUnaVez && (
        <span className="gx-unavez">
          Una sola vez · ábrela en tu celular (WhatsApp no la entrega a otros dispositivos)
          {!m.deMi && (
            <label className="gx-opcion" style={{ display: `inline-flex`, marginLeft: 8 }}>
              <input type="checkbox" checked={m.abierto} onChange={() => acciones.anotar(m, { abierto: !m.abierto })} /> Ya la abrí
            </label>
          )}
        </span>
      )}
      <div className="gx-contenido">{especial ?? <ContenidoMensaje m={m} cliente={cliente} />}</div>
      <Reacciones lista={m.reacciones} onReaccionar={m.citable ? (e) => acciones.reaccionar(m, e) : null} />
      {m.nota && !anotando && <button type="button" className="gx-nota" title="Editar nota" onClick={() => setAnotando(true)}>📝 {m.nota}</button>}
      {anotando && <EditorNota m={m} api={acciones.api} onListo={() => { setAnotando(false); acciones.recargar(); }} />}
      {(eliminado || m.editadoEn) && (
        <div className="gx-marcas">
          {eliminado && <span className="error">Eliminado por {quien} · {horaDe(m.eliminadoEn)}</span>}
          {m.editadoEn && (
            <button type="button" aria-expanded={verVersiones} onClick={() => setVerVersiones(!verVersiones)}>
              editado{m.versiones?.length ? ` · ${m.versiones.length} ${m.versiones.length === 1 ? `versión anterior` : `versiones anteriores`}` : ``}
            </button>
          )}
        </div>
      )}
      {verVersiones && m.versiones?.length > 0 && (
        <ul className="gx-versiones">
          {m.versiones.map((v, i) => <li key={i}>{horaDe(v.reemplazadoEn)} — «{v.texto ?? `sin texto`}»</li>)}
        </ul>
      )}
      <time>{horaDe(m.enviadoEn)}</time>
    </div>
  );
}

function Tanda({ t, chat, cliente, acciones }) {
  const nombre = t.deMi ? `Tú` : chat.esGrupo ? (t.autor ?? `Alguien`) : chat.nombre;
  return (
    <div className={`gx-tanda${t.deMi ? ` mia` : ``}`}>
      <span className="gx-avatar" style={{ "--av": t.deMi ? `var(--accent)` : colorDe(nombre) }} aria-hidden="true">
        {t.deMi ? `Tú` : iniciales(nombre)}
      </span>
      <div className="gx-tanda-cab">{nombre} <time>{horaDe(t.desde)}</time></div>
      {t.mensajes.map((m) => <Mensaje key={m.id} m={m} chat={chat} cliente={cliente} acciones={acciones} />)}
    </div>
  );
}

/** Lo que mandaste y todavía no está en el hilo: enviando, programado o con error. */
function Salientes({ lista, onCancelar }) {
  const visibles = lista.filter((s) => s.estado !== `enviado`);
  if (!visibles.length) return null;
  return (
    <div className="gx-salientes" aria-label="Por enviar">
      {visibles.map((s) => (
        <div key={s.id} className={`gx-saliente${s.estado === `error` ? ` error` : ``}`}>
          <span>{s.texto}</span>
          <small>
            {s.estado === `programado` ? `Programado · ${fechaHora(s.programadoPara)}`
              : s.estado === `error` ? `No se envió: ${s.error}` : `enviando…`}
          </small>
          {(s.estado === `programado` || s.estado === `error`) && !s.temporal && (
            <button type="button" className="gx-boton chico" onClick={() => onCancelar(s)}>
              {s.estado === `error` ? `Quitar` : `Cancelar`}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/** El reloj de la caja: elegir cuándo sale el mensaje. */
function Programar({ deshabilitado, onElegir }) {
  const [abierto, setAbierto] = useState(false);
  const [otra, setOtra] = useState(``);
  const ref = useRef(null);
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    const tecla = (e) => { if (e.key === `Escape`) { e.stopPropagation(); setAbierto(false); } };
    document.addEventListener(`pointerdown`, fuera);
    document.addEventListener(`keydown`, tecla, true);
    return () => { document.removeEventListener(`pointerdown`, fuera); document.removeEventListener(`keydown`, tecla, true); };
  }, [abierto]);
  const elegir = (cuando) => { setAbierto(false); setOtra(``); onElegir(cuando); };
  return (
    <span className="gx-ancla" ref={ref}>
      <button type="button" className="gx-icono" style={{ height: 34, width: 34 }} aria-label="Programar envío" title="Programar envío"
        aria-haspopup="menu" aria-expanded={abierto} disabled={deshabilitado} onClick={() => setAbierto(!abierto)}><IconoReloj /></button>
      {abierto && (
        <div className="gx-menu derecha gx-programar" role="menu" style={{ top: `auto`, bottom: `calc(100% + 8px)` }}>
          <span className="gx-titulo-menu">Enviar más tarde</span>
          {opcionesProgramar().map((o) => (
            <button key={o.texto} type="button" role="menuitem" onClick={() => elegir(o.cuando)}>
              {o.texto} <small className="gx-texto">· {fechaHora(o.cuando)}</small>
            </button>
          ))}
          <hr />
          <form className="gx-fila" style={{ padding: `4px 6px` }} onSubmit={(e) => { e.preventDefault(); if (otra) elegir(new Date(otra)); }}>
            <input type="datetime-local" className="gx-entrada" aria-label="Otra fecha y hora" value={otra} onChange={(e) => setOtra(e.target.value)} />
            <button type="submit" className="gx-boton chico" disabled={!otra}>Programar</button>
          </form>
        </div>
      )}
    </span>
  );
}

/** Los recordatorios pendientes de este chat, arriba de la caja. */
function RecordatoriosChat({ lista, onHecho }) {
  if (!lista?.length) return null;
  return (
    <div className="gx-recordatorios" aria-label="Recordatorios">
      {lista.map((r) => (
        <div key={r.id} className={`gx-recordatorio${r.vencido ? ` vencido` : ``}`}>
          <span>⏰ {r.vencido ? `Te pediste responder` : `Recordatorio`} · {fechaHora(r.para)}{r.nota ? ` · ${r.nota}` : ``}{r.mensaje ? ` · «${r.mensaje}»` : ``}</span>
          <button type="button" className="gx-boton chico" onClick={() => onHecho(r)}>Hecho</button>
        </div>
      ))}
    </div>
  );
}

export function ConversacionGestor({ chat, cliente, modosApi, plantillas = [], recordatorios = [], onRecordatorios, onVolver, onFallo, onCambiarChat, infoAbierta, onInfo }) {
  const [mensajes, setMensajes] = useState(null);
  const [anteriores, setAnteriores] = useState([]);
  const [hayMas, setHayMas] = useState(true);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [salientes, setSalientes] = useState([]);
  const [temporales, setTemporales] = useState([]);
  const [texto, setTexto] = useState(``);
  const [errorEnvio, setErrorEnvio] = useState(``);
  const [sugerida, setSugerida] = useState(0);
  const [respondiendo, setRespondiendo] = useState(null);
  const [adjunto, setAdjunto] = useState(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [modal, setModal] = useState(null);
  const [aviso, setAviso] = useState(``);
  useEffect(() => { if (!aviso) return undefined; const t = setTimeout(() => setAviso(``), 6000); return () => clearTimeout(t); }, [aviso]);
  const elegirArchivo = useRef(null);
  const hilo = useRef(null);
  const pegadoAbajo = useRef(true);
  const caja = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const [recientes, sal] = await Promise.all([modosApi.mensajes(chat.id), modosApi.salientes(chat.id)]);
      setMensajes(recientes);
      setSalientes(sal);
      setTemporales([]);
    } catch (e) {
      onFallo(e);
    }
  }, [chat.id, modosApi, onFallo]);

  useEffect(() => {
    setMensajes(null); setAnteriores([]); setHayMas(true); setSalientes([]); setTemporales([]); pegadoAbajo.current = true;
    cargar();
    const t = setInterval(cargar, 3e3);
    return () => clearInterval(t);
  }, [cargar]);

  const todos = useMemo(() => {
    if (!mensajes) return null;
    const ids = new Set(mensajes.map((m) => m.id));
    return [...anteriores.filter((m) => !ids.has(m.id)), ...mensajes];
  }, [mensajes, anteriores]);
  const dias = useMemo(() => (todos ? agruparHilo(todos) : []), [todos]);
  const porEnviar = useMemo(() => [...salientes, ...temporales], [salientes, temporales]);

  // Baja solo si ya estabas abajo: leer algo viejo no se interrumpe.
  useEffect(() => {
    const el = hilo.current;
    if (el && pegadoAbajo.current) el.scrollTop = el.scrollHeight;
  }, [todos?.length, porEnviar.length]);
  const alDesplazar = () => {
    const el = hilo.current;
    pegadoAbajo.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const cargarAnteriores = async () => {
    const primero = todos?.[0];
    if (!primero) return;
    setCargandoMas(true);
    const el = hilo.current;
    const alto = el.scrollHeight;
    try {
      const viejos = await modosApi.mensajes(chat.id, primero.enviadoEn);
      setHayMas(viejos.length >= 80);
      setAnteriores((a) => [...viejos, ...a]);
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight - alto; });
    } catch (e) {
      onFallo(e);
    } finally {
      setCargandoMas(false);
    }
  };

  const adjuntar = async (archivo) => {
    if (!archivo) return;
    setErrorEnvio(``);
    try {
      const datos = await leerArchivo(archivo);
      setAdjunto({ ...datos, bytes: archivo.size, vista: archivo.type.startsWith(`image/`) ? URL.createObjectURL(archivo) : null });
      caja.current?.focus();
    } catch (e) { setErrorEnvio(e.message); }
  };
  useEffect(() => () => { if (adjunto?.vista) URL.revokeObjectURL(adjunto.vista); }, [adjunto]);

  const enviar = async (programadoPara = null) => {
    const t = texto.trim();
    if (!t && !adjunto) return;
    setTexto(``); setErrorEnvio(``);
    const archivo = adjunto ? { nombre: adjunto.nombre, mime: adjunto.mime, base64: adjunto.base64 } : null;
    const cita = respondiendo;
    setAdjunto(null); setRespondiendo(null);
    const temporal = {
      id: `t-${Date.now()}`, texto: archivo ? `📎 ${archivo.nombre}${t ? ` · ${t}` : ``}` : t, temporal: true,
      estado: programadoPara ? `programado` : `pendiente`, programadoPara,
    };
    setTemporales((ts) => [...ts, temporal]);
    pegadoAbajo.current = true;
    try {
      await modosApi.enviar(chat.id, t, programadoPara, { ...(archivo ? { archivo } : {}), ...(cita ? { citaId: cita.id } : {}) });
      setSalientes(await modosApi.salientes(chat.id));
      setTemporales((ts) => ts.filter((x) => x !== temporal));
    } catch (e) {
      setTemporales((ts) => ts.filter((x) => x !== temporal));
      setTexto(t);
      setRespondiendo(cita);
      setErrorEnvio(e.message || `No se pudo enviar.`);
    }
    caja.current?.focus();
  };

  const acciones = useMemo(() => ({
    reaccionar: async (m, emoji) => {
      try { await modosApi.reaccionar(m.id, emoji); setSalientes(await modosApi.salientes(chat.id)); } catch (e) { setErrorEnvio(e.message); }
    },
    responder: (m) => { setRespondiendo(m); caja.current?.focus(); },
    recordar: (m) => setModal({ tipo: `recordar`, mensaje: m }),
    tarea: (m) => setModal({ tipo: `tarea`, mensaje: m }),
    anotar: async (m, cambios) => { try { await modosApi.anotar(m.id, cambios); cargar(); } catch (e) { setErrorEnvio(e.message); } },
    recargar: () => cargar(),
    api: modosApi,
  }), [modosApi, chat.id, cargar]);
  const [buscando, setBuscando] = useState(false);

  const hecho = async (r) => {
    try { await modosApi.recordatorioHecho(r.id); onRecordatorios?.(); } catch (e) { setErrorEnvio(e.message); }
  };

  const cancelar = async (s) => {
    setSalientes((ss) => ss.filter((x) => x.id !== s.id));
    try { await modosApi.cancelar(s.id); } catch (e) { setErrorEnvio(e.message); cargar(); }
  };

  // Respuestas rápidas: «/atajo» al principio de la caja.
  const consulta = /^\/([\w-]*)$/.exec(texto)?.[1];
  const sugerencias = consulta == null ? [] : plantillas.filter((p) => p.atajo.startsWith(consulta.toLowerCase())).slice(0, 6);
  useEffect(() => { setSugerida(0); }, [consulta]);
  const usarPlantilla = (p) => { setTexto(p.texto); caja.current?.focus(); };

  const alTeclear = (e) => {
    if (sugerencias.length) {
      if (e.key === `ArrowDown` || e.key === `ArrowUp`) {
        e.preventDefault();
        setSugerida((i) => (i + (e.key === `ArrowDown` ? 1 : -1) + sugerencias.length) % sugerencias.length);
        return;
      }
      if (e.key === `Enter` || e.key === `Tab`) { e.preventDefault(); usarPlantilla(sugerencias[sugerida]); return; }
      if (e.key === `Escape`) { e.preventDefault(); e.stopPropagation(); setTexto(``); return; }
    }
    if (e.key === `Escape` && (respondiendo || adjunto)) { e.preventDefault(); e.stopPropagation(); setRespondiendo(null); setAdjunto(null); return; }
    if (e.key === `Enter` && !e.shiftKey) { e.preventDefault(); enviar(); }
  };

  // La caja crece con el texto.
  useEffect(() => {
    const el = caja.current;
    if (!el) return;
    el.style.height = `auto`;
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [texto]);
  useEffect(() => { caja.current?.focus(); }, [chat.id]);

  return (
    <section className="gx-conv" aria-label={`Conversación con ${chat.nombre}`}>
      <header className="gx-conv-cab">
        <button type="button" className="gx-icono gx-solo-movil" aria-label="Volver a la lista" onClick={onVolver}>
          <IconoVolver className="h-4 w-4" />
        </button>
        <Avatar chat={chat} api={modosApi} grande />
        <div className="gx-titulo">
          <h2>{chat.nombre}</h2>
          <p>
            {chat.esGrupo ? <span>Grupo</span> : chat.numero && chat.numero !== chat.nombre && <span>{chat.numero}</span>}
            {chat.nombreWhatsApp && <span>· en WhatsApp: {chat.nombreWhatsApp}</span>}
            {chat.etiquetas?.map((e) => <span key={e} className="gx-etiqueta">{e}</span>)}
          </p>
        </div>
        <button type="button" className={`gx-icono${chat.vip ? ` on` : ``}`} aria-pressed={chat.vip}
          title={chat.vip ? `Quitar de VIP` : `Marcar VIP: se ve en cualquier modo`} aria-label="VIP"
          onClick={() => onCambiarChat({ vip: !chat.vip })}><IconoEstrella /></button>
        <button type="button" className={`gx-icono pin${chat.fijado ? ` on` : ``}`} aria-pressed={chat.fijado}
          title={chat.fijado ? `Desfijar` : `Fijar arriba`} aria-label="Fijar"
          onClick={() => onCambiarChat({ fijado: !chat.fijado })}><IconoPin /></button>
        <button type="button" className={`gx-icono${infoAbierta ? ` on pin` : ``}`} aria-pressed={infoAbierta}
          title="Datos del contacto" aria-label="Datos del contacto" onClick={onInfo}><IconoInfo /></button>
        <button type="button" className="gx-icono" title="Recordarme responder" aria-label="Recordarme responder"
          onClick={() => setModal({ tipo: `recordar`, mensaje: null })}>⏰</button>
        <button type="button" className={`gx-icono${buscando ? ` on pin` : ``}`} title="Buscar en este chat" aria-label="Buscar en este chat"
          aria-pressed={buscando} onClick={() => setBuscando(!buscando)}>🔍</button>
      </header>
      {buscando && <BuscadorChat chat={chat} api={modosApi} onCerrar={() => setBuscando(false)} />}

      <div className="gx-hilo" ref={hilo} onScroll={alDesplazar}>
        <div className="gx-hilo-ancho">
          {!todos && <div className="gx-vacio"><Girando className="h-5 w-5 animate-spin" /></div>}
          {todos && todos.length === 0 && !porEnviar.length && <div className="gx-vacio">Todavía no hay mensajes. Escribe el primero abajo.</div>}
          {todos && todos.length >= 80 && hayMas && (
            <div className="gx-anteriores">
              <button type="button" className="gx-boton chico" disabled={cargandoMas} onClick={cargarAnteriores}>
                {cargandoMas ? `Cargando…` : `Cargar mensajes anteriores`}
              </button>
            </div>
          )}
          {dias.map((d) => (
            <div key={d.dia}>
              <div className="gx-separador-dia">{d.dia}</div>
              {d.tandas.map((t) => <Tanda key={`${t.mensajes[0].id}`} t={t} chat={chat} cliente={cliente} acciones={acciones} />)}
            </div>
          ))}
        </div>
        <Salientes lista={porEnviar} onCancelar={cancelar} />
        <RecordatoriosChat lista={recordatorios} onHecho={hecho} />
      </div>

      <form className={`gx-redactor${arrastrando ? ` gx-arrastrar` : ``}`} onSubmit={(e) => { e.preventDefault(); enviar(); }}
        onDragOver={(e) => { if ([...e.dataTransfer.types].includes(`Files`)) { e.preventDefault(); setArrastrando(true); } }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={(e) => { e.preventDefault(); setArrastrando(false); adjuntar(e.dataTransfer.files?.[0]); }}>
        {respondiendo && (
          <div className="gx-respondiendo">↩ <span>Respondiendo a {respondiendo.deMi ? `ti` : chat.esGrupo ? (respondiendo.autor ?? `mensaje`) : chat.nombre}: «{respondiendo.texto ?? `adjunto`}»</span>
            <button type="button" className="gx-boton chico" onClick={() => setRespondiendo(null)}>Quitar</button></div>
        )}
        {adjunto && (
          <div className="gx-adjunto">
            {adjunto.vista ? <img src={adjunto.vista} alt="" /> : <span aria-hidden="true" style={{ flex: `none` }}>📎</span>}
            <span>{adjunto.nombre} · {tamano(adjunto.bytes)}{texto.trim() ? `` : ` · puedes escribir un pie`}</span>
            <button type="button" className="gx-boton chico" onClick={() => setAdjunto(null)}>Quitar</button>
          </div>
        )}
        {sugerencias.length > 0 && (
          <div className="gx-sugerencias" role="listbox" aria-label="Respuestas rápidas">
            {sugerencias.map((p, i) => (
              <button key={p.id} type="button" role="option" aria-selected={i === sugerida}
                onMouseDown={(e) => { e.preventDefault(); usarPlantilla(p); }}>
                <span>/{p.atajo}</span><span>{p.texto}</span>
              </button>
            ))}
          </div>
        )}
        <div className="gx-redactor-caja">
          <textarea ref={caja} rows={1} aria-label="Mensaje" placeholder={adjunto ? `Pie de foto (opcional)` : `Escribe a ${chat.nombre}`}
            value={texto} onChange={(e) => setTexto(e.target.value)} onKeyDown={alTeclear}
            onPaste={(e) => { const f = [...(e.clipboardData?.files ?? [])][0]; if (f) { e.preventDefault(); adjuntar(f); } }} />
          <input ref={elegirArchivo} type="file" hidden onChange={(e) => { adjuntar(e.target.files?.[0]); e.target.value = ``; }} />
          <button type="button" className="gx-icono" style={{ height: 34, width: 34 }} title="Adjuntar foto, documento o audio (hasta 16 MB)"
            aria-label="Adjuntar archivo" onClick={() => elegirArchivo.current?.click()}>📎</button>
          <Programar deshabilitado={!texto.trim() && !adjunto} onElegir={(cuando) => enviar(cuando)} />
          <button type="submit" className="gx-boton primario" disabled={!texto.trim() && !adjunto} aria-label="Enviar"><IconoEnviar /> Enviar</button>
        </div>
        <div className="gx-redactor-ayuda">
          {errorEnvio ? <span className="gx-error" role="alert">{errorEnvio}</span> : aviso ? <span role="status">✓ {aviso}</span> : <>
            <span><kbd>Enter</kbd> envía</span><span><kbd>Shift</kbd>+<kbd>Enter</kbd> salto</span>
            <span><kbd>/</kbd> respuestas rápidas</span><span>📎 o arrastra/pega un archivo</span>
          </>}
        </div>
      </form>
      {modal?.tipo === `recordar` && (
        <ModalRecordar api={modosApi} chat={chat} mensaje={modal.mensaje} onCerrar={() => setModal(null)}
          onListo={() => { setModal(null); setAviso(`Te lo recuerdo.`); onRecordatorios?.(); }} />
      )}
      {modal?.tipo === `tarea` && (
        <ModalTarea chat={chat} mensaje={modal.mensaje} onCerrar={() => setModal(null)}
          onListo={() => { setModal(null); setAviso(`Tarea creada en el planner.`); }} />
      )}
    </section>
  );
}
